import { useMutation } from '@tanstack/react-query';
import { runBulkOp } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { BulkImportModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import { Messages } from '../../../utils/message-constants';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useState } from 'react';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import Typography from '@mui/material/Typography';

interface RunConfirmBulkOpDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedBulkOp: BulkImportModel;
  bulkImportRole?: string;
  changeLogRoles: string[];
}

export default function RunConfirmBulkOpDialog({
  open,
  handleClose,
  selectedBulkOp,
  bulkImportRole = '',
  changeLogRoles,
}: RunConfirmBulkOpDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { patchLastRan } = useBulkOpActions();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const needsChangeLog = isAbleToSeeUIElement(changeLogRoles, bulkImportRole);

  const { mutate, isPending } = useMutation({
    mutationFn: () => runBulkOp(selectedBulkOp, null, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.BulkOperations.TaskStarted,
      });
      patchLastRan(selectedBulkOp.id);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Running('this bulk operation'),
      });
    },
    onSettled: () => {
      createEvent(analytics.import_export.bulk_operations.run_bulk_operation, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.bulkOperations,
        bulkOperationType: selectedBulkOp.bulkImport,
      });
    },
  });

  const handleSubmit = () => {
    if (needsChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  };

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title="Run Bulk Operation"
        dialogContent={
          <Typography>
            Run <strong>{selectedBulkOp.name}</strong>. This may take a few
            minutes depending on the data size.
          </Typography>
        }
        primaryButton={{
          title: 'Run Bulk Operation',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => mutate()}
        mutateLoading={isPending}
      />
    </>
  );
}
